/**
 * Inline announcement-bar countdown in America/Los_Angeles (Pacific Time).
 * Expires on the merchant-chosen date and hour.
 */
(function () {
  const TIMEZONE = 'America/Los_Angeles';

  function getZonedParts(date, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
    const map = {};
    formatter.formatToParts(date).forEach(function (part) {
      if (part.type !== 'literal') map[part.type] = Number(part.value);
    });
    return map;
  }

  function zonedDateToUtc(parts, timeZone) {
    const utcGuess = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );
    const asZoned = getZonedParts(new Date(utcGuess), timeZone);
    const asUtc = Date.UTC(
      asZoned.year,
      asZoned.month - 1,
      asZoned.day,
      asZoned.hour,
      asZoned.minute,
      asZoned.second
    );
    return utcGuess - (asUtc - utcGuess);
  }

  function parseDateParts(dateStr) {
    const match = String(dateStr || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return null;
    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3])
    };
  }

  function to24Hour(hour, period) {
    let value = Number(hour);
    if (Number.isNaN(value)) value = 0;
    const meridiem = String(period || '').toLowerCase();
    if (meridiem === 'am') return value === 12 ? 0 : value;
    if (meridiem === 'pm') return value === 12 ? 12 : value + 12;
    return value;
  }

  function endTimePT(dateStr, hour, minute, period) {
    const date = parseDateParts(dateStr);
    if (!date) return NaN;
    return zonedDateToUtc(
      {
        year: date.year,
        month: date.month,
        day: date.day,
        hour: to24Hour(hour, period),
        minute: Number(minute) || 0,
        second: 0
      },
      TIMEZONE
    );
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function plural(count, singular, pluralLabel) {
    return count === 1 ? singular : pluralLabel;
  }

  function formatRemaining(ms, format) {
    const remaining = Math.max(0, ms);
    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const totalHours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (format === 'hours') {
      if (totalHours >= 1) return totalHours + ' ' + plural(totalHours, 'hour', 'hours');
      if (minutes >= 1) return minutes + ' ' + plural(minutes, 'minute', 'minutes');
      return 'less than 1 minute';
    }

    if (format === 'hm') {
      return pad(totalHours) + ':' + pad(minutes);
    }

    if (format === 'xhym') {
      return pad(totalHours) + 'h ' + pad(minutes) + 'm';
    }

    if (format === 'compact') {
      if (days >= 1) return days + 'd ' + hours + 'h ' + pad(minutes) + 'm';
      if (totalHours >= 1) return totalHours + 'h ' + pad(minutes) + 'm';
      return minutes + 'm ' + pad(seconds) + 's';
    }

    if (days >= 1) return days + 'd ' + pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
    return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
  }

  function spokenRemaining(ms) {
    const remaining = Math.max(0, ms);
    const totalSeconds = Math.floor(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const parts = [];
    if (hours >= 1) parts.push(hours + ' ' + plural(hours, 'hour', 'hours'));
    if (minutes >= 1) parts.push(minutes + ' ' + plural(minutes, 'minute', 'minutes'));
    if (!parts.length) return 'Offer has ended';
    return parts.join(' ') + ' remaining';
  }

  if (typeof customElements === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = { endTimePT, formatRemaining, TIMEZONE, to24Hour };
    }
    return;
  }

  if (!customElements.get('announce-countdown')) {
    customElements.define(
      'announce-countdown',
      class AnnounceCountdown extends HTMLElement {
        connectedCallback() {
          this.format = this.getAttribute('data-format') || 'hms';
          this.hideWhenExpired = this.getAttribute('data-hide-when-expired') === 'true';
          this.endAt = endTimePT(
            this.getAttribute('data-end-date'),
            this.getAttribute('data-end-hour'),
            this.getAttribute('data-end-minute'),
            this.getAttribute('data-end-period')
          );
          this.valueEl = this.querySelector('[data-countdown-value]');
          this.endedEl = this.querySelector('[data-countdown-ended]');
          this.tick = this.tick.bind(this);
          this.lastSpoken = '';
          this.expired = false;
          this.tick();
          if (!this.expired) this.interval = window.setInterval(this.tick, 1000);
        }

        disconnectedCallback() {
          window.clearInterval(this.interval);
        }

        expire() {
          this.expired = true;
          window.clearInterval(this.interval);
          if (this.hideWhenExpired) {
            const slide = this.closest('[x-ref]') || this.closest('[data-announcement-bar] > *');
            if (slide) slide.hidden = true;
            document.dispatchEvent(new CustomEvent('theme:resize', { bubbles: true }));
            return;
          }
          if (this.endedEl) {
            if (this.valueEl) this.valueEl.hidden = true;
            this.endedEl.hidden = false;
          } else if (this.valueEl) {
            this.valueEl.textContent = formatRemaining(0, this.format);
          }
          this.setAttribute('aria-label', 'Offer has ended');
        }

        tick() {
          if (Number.isNaN(this.endAt)) {
            this.expire();
            return;
          }
          const remaining = this.endAt - Date.now();
          if (remaining <= 0) {
            this.expire();
            return;
          }
          const label = formatRemaining(remaining, this.format);
          if (this.valueEl && this.valueEl.textContent !== label) {
            this.valueEl.textContent = label;
          }
          const spoken = spokenRemaining(remaining);
          if (spoken !== this.lastSpoken) {
            this.setAttribute('aria-label', spoken);
            this.lastSpoken = spoken;
          }
        }
      }
    );
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { endTimePT, formatRemaining, TIMEZONE, to24Hour };
  }
})();
